/**
 * useSheetData — primary data-fetch hook (v5 — single Firestore listener)
 *
 * Uses subscribeHistoricalTelemetry() which queries the `date` string field
 * (what the Pi actually writes) instead of a Firestore `timestamp` field
 * (which the Pi does NOT write, causing the previous version to return 0 rows).
 *
 * One onSnapshot listener covers both the 30-day historical window and live
 * updates — no separate polling or second subscription needed.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  subscribeHistoricalTelemetry,
  type FetchResult,
} from '@/api/firebaseClient';
import { useAuth } from '@/auth/AuthContext';
import type { TelemetryRow, RawMeterRow, MetaRow } from '@/types';
import { computeAllDerived } from '@/lib/derivedEngine';
import { getTariff } from '@/tariffs';

export type LoadStatus = 'idle' | 'loading' | 'refreshing' | 'success' | 'error';

export interface UseSheetData {
  rows:      TelemetryRow[];
  fetchedAt: Date | null;
  status:    LoadStatus;
  error:     string | null;
  counts:    FetchResult['counts'] | null;
  refetch:   () => void;
}

export function useSheetData(): UseSheetData {
  const { selectedSheet, firebaseAuthReady } = useAuth();

  const [rows,      setRows]      = useState<TelemetryRow[]>([]);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
  const [status,    setStatus]    = useState<LoadStatus>('idle');
  const [error,     setError]     = useState<string | null>(null);
  const [counts,    setCounts]    = useState<FetchResult['counts'] | null>(null);

  const unsubRef = useRef<(() => void) | null>(null);

  const subscribe = useCallback(() => {
    if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
    if (!selectedSheet) return;

    setStatus('loading');

    const tariff = getTariff(selectedSheet.stateCode);
    const contractDemandKva =
      selectedSheet.meta?.contractDemandKva ?? tariff.defaultContractDemandKva;

    unsubRef.current = subscribeHistoricalTelemetry(
      selectedSheet,
      new Date(), // billing month: the calendar month containing today
      (rawRows: (RawMeterRow & MetaRow)[]) => {
        const derived = computeAllDerived(rawRows, contractDemandKva, tariff);
        setRows(derived);
        setFetchedAt(new Date());
        setCounts({ rows: derived.length });
        setStatus('success');
        setError(null);
      },
      (err: Error) => {
        setError(err.message);
        setStatus('error');
      },
    );
  }, [selectedSheet]);

  // Wait for Firebase Auth to complete before opening the Firestore listener.
  // Without this gate, a page-refresh with a cached session fires the listener
  // before signInWithEmailAndPassword finishes — and the rules deny the read.
  useEffect(() => {
    if (!selectedSheet) {
      if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
      setRows([]);
      return;
    }
    if (!firebaseAuthReady) return;
    subscribe();
    return () => { if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSheet?.id, firebaseAuthReady]);

  return {
    rows,
    fetchedAt,
    status,
    error,
    counts,
    refetch: subscribe,
  };
}
